import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { secondAxios } from "../../../../api/axios";
import { getItps } from "../../AllEquipments/CommonFeatures/PCSTab/ITP/api";

const AmsModuleDataContext = createContext(null);

const amsModuleDataCache = new Map();

const initialState = {
  plant: null,
  projects: [],
  itpsData: [],
  isLoading: false,
  isLoaded: false,
  isError: false,
  error: null,
};

function normalizePlantResponse(response) {
  return response?.data?.id ? response.data : null;
}

function normalizeProjectsResponse(response) {
  if (Array.isArray(response?.data?.projects)) {
    return response.data.projects;
  }

  if (Array.isArray(response?.projects)) {
    return response.projects;
  }

  return [];
}

function normalizeItpsResponse(response) {
  if (Array.isArray(response?.data?.data)) {
    return response.data.data;
  }

  if (Array.isArray(response?.data)) {
    return response.data;
  }

  if (Array.isArray(response?.itps)) {
    return response.itps;
  }

  if (Array.isArray(response)) {
    return response;
  }

  return [];
}

function getCachedData(plantId) {
  if (!plantId) return null;
  return amsModuleDataCache.get(String(plantId)) || null;
}

function setCachedData(plantId, data) {
  if (!plantId) return;
  amsModuleDataCache.set(String(plantId), data);
}

export function AmsModuleDataProvider({ plantId, children }) {
  const currentPlantId = plantId ? String(plantId) : "";

  const activeRequestRef = useRef(null);
  const requestVersionRef = useRef(0);

  const [state, setState] = useState(() => {
    const cached = getCachedData(currentPlantId);

    if (cached) {
      return {
        ...initialState,
        ...cached,
        isLoaded: true,
      };
    }

    return initialState;
  });

  const loadAmsData = useCallback(
    async ({ force = false } = {}) => {
      if (!currentPlantId) {
        setState(initialState);
        return initialState;
      }

      const cached = getCachedData(currentPlantId);

      if (cached && !force) {
        const cachedState = {
          ...initialState,
          ...cached,
          isLoaded: true,
        };

        setState(cachedState);
        return cachedState;
      }

      if (activeRequestRef.current && !force) {
        return activeRequestRef.current;
      }

      const requestVersion = requestVersionRef.current + 1;
      requestVersionRef.current = requestVersion;

      setState((prev) => ({
        ...prev,
        isLoading: true,
        isError: false,
        error: null,
      }));

      const request = Promise.all([
        secondAxios.get(`/plant/${currentPlantId}`),
        secondAxios.get(`/plant/${currentPlantId}/mcs`),
        getItps(),
      ])
        .then(([plantResponse, mcsResponse, itpsResponse]) => {
          const nextData = {
            plant: normalizePlantResponse(plantResponse),
            projects: normalizeProjectsResponse(mcsResponse),
            itpsData: normalizeItpsResponse(itpsResponse),
          };

          setCachedData(currentPlantId, nextData);

          if (requestVersionRef.current === requestVersion) {
            setState({
              ...initialState,
              ...nextData,
              isLoaded: true,
            });
          }

          return {
            ...initialState,
            ...nextData,
            isLoaded: true,
          };
        })
        .catch((error) => {
          console.error("Error loading AMS module data:", error);

          if (requestVersionRef.current === requestVersion) {
            setState((prev) => ({
              ...prev,
              isLoading: false,
              isLoaded: false,
              isError: true,
              error,
            }));
          }

          throw error;
        })
        .finally(() => {
          if (requestVersionRef.current === requestVersion) {
            activeRequestRef.current = null;
          }
        });

      activeRequestRef.current = request;
      return request;
    },
    [currentPlantId],
  );

  useEffect(() => {
    loadAmsData().catch(() => {});
  }, [loadAmsData]);

  const refreshAmsData = useCallback(() => {
    return loadAmsData({ force: true });
  }, [loadAmsData]);

  const value = useMemo(
    () => ({
      ...state,
      plantId: currentPlantId,
      ensureAmsDataLoaded: loadAmsData,
      refreshAmsData,
    }),
    [state, currentPlantId, loadAmsData, refreshAmsData],
  );

  return (
    <AmsModuleDataContext.Provider value={value}>
      {children}
    </AmsModuleDataContext.Provider>
  );
}

export function useAmsModuleData() {
  const context = useContext(AmsModuleDataContext);

  if (!context) {
    throw new Error(
      "useAmsModuleData must be used inside AmsModuleDataProvider",
    );
  }

  return context;
}
